// Authenticated Layout
// ------------------------------------------------------------------------------
import { createFileRoute, defaultStringifySearch, Navigate, Outlet, useLocation } from '@tanstack/react-router';
import LayoutDefault from '@/layout/default';
import { useUser } from '@/hooks/queries/auth';
import { useGetCurrentUserDetails } from '@/hooks/queries/user';
import { getSafePostLoginRedirect } from '@/lib/authRedirect';
import { TabCommentNotificationManager } from '@/components/notifications/TabCommentNotificationManager';
import { AccountDisabledScreen } from '@/components/auth/AccountDisabledScreen';
// ------------------------------------------------------------------------------


/**
 * The Authorized Route for handling logged-in users.
 * 
 * This route component ensures that only authenticated users can access 
 * the content within the layout. If a user is not authenticated, 
 * they will be redirected to the login page. The layout is wrapped 
 * in a default layout component that provides a consistent UI structure.
 */
export const Route = createFileRoute('/_authenticated-layout')({
  component: RouteComponent,
});

// ------------------------------------------------------------------------------

/**
 * RouteComponent is the main component for the authenticated layout.
 * It serves as a wrapper for the application's layout and ensures that
 * only authenticated users can access the content within. If a user
 * is not authenticated, they will be redirected to the login page.
 * 
 * The component utilizes the LayoutDefault component to provide a
 * consistent UI structure across the application. The Outlet component
 * is used to render the child routes defined in the router.
 */
function RouteComponent() {
  // Retrieve the current user from the authentication state
  const user = useUser(state => state.user);
  const location = useLocation();
  const { data: currentUserDetails } = useGetCurrentUserDetails();

  // Redirect to the login page if the user is not authenticated
  if (!user) {
    const redirectTo = getSafePostLoginRedirect(
      `${location.pathname}${defaultStringifySearch(location.search)}`,
    );
    return (
      <Navigate
        to="/authentikLogin"
        replace
        search={{
          redirectTo,
        }}
      />
    );
  }

  const isAccountDisabled =
    user.isEnabled === false || currentUserDetails?.data?.isEnabled === false;

  if (isAccountDisabled) {
    return <AccountDisabledScreen />;
  }

  // If user is logged in then show the route.
  return (
    <LayoutDefault>
      <TabCommentNotificationManager />
      <Outlet />
    </LayoutDefault>
  );
};
